import { Matrix4, Quaternion, Vector3 } from 'three';
export interface TrackedPose { p: [number, number, number]; q: [number, number, number, number]; }
export interface AvatarTracking { v: 1; time: number; referenceHeight?: number; head: TrackedPose; left: TrackedPose | null; right: TrackedPose | null; }
const tuple = (v: unknown, n: number) => Array.isArray(v) && v.length === n && v.every(x => typeof x === 'number' && Number.isFinite(x));
function validPose(v: any): v is TrackedPose {
  if (!v || !tuple(v.p, 3) || !tuple(v.q, 4) || v.p.some((n: number) => Math.abs(n) > 5)) return false;
  const length = Math.hypot(...v.q); return length > .9 && length < 1.1;
}
export function decodeTracking(v: any): AvatarTracking | null {
  if (!v || v.v !== 1 || !Number.isFinite(v.time) || !validPose(v.head) || (v.left !== null && !validPose(v.left)) || (v.right !== null && !validPose(v.right))) return null;
  if (v.referenceHeight !== undefined && (!Number.isFinite(v.referenceHeight) || v.referenceHeight < .65 || v.referenceHeight > 2.5)) return null;
  return v;
}
/** Convert a reference-space pose to avatar-root space, applying the origin once. */
export function relativeTrackingPose(transform: XRRigidTransform, originWorld: Matrix4, avatarWorld: Matrix4): TrackedPose {
  const matrix = new Matrix4().fromArray(transform.matrix);
  matrix.premultiply(originWorld).premultiply(avatarWorld.clone().invert());
  const p = new Vector3(), q = new Quaternion(), s = new Vector3(); matrix.decompose(p, q, s);
  return { p: p.toArray(), q: q.normalize().toArray() };
}
function blendPose(a: TrackedPose, b: TrackedPose, t: number): TrackedPose {
  return { p: new Vector3().fromArray(a.p).lerp(new Vector3().fromArray(b.p), t).toArray(), q: new Quaternion().fromArray(a.q).slerp(new Quaternion().fromArray(b.q), t).normalize().toArray() };
}
/** Local arrival-time buffering avoids relying on synchronized wall clocks. */
export class AvatarTrackingBuffer {
  private samples: { pose: AvatarTracking; at: number }[] = [];
  push(pose: AvatarTracking | null, at: number) {
    if (!pose) { this.samples = []; return; }
    const last = this.samples.at(-1);
    if (last && pose.time <= last.pose.time) return;
    this.samples.push({ pose, at }); if (this.samples.length > 8) this.samples.shift();
  }
  sample(now: number): AvatarTracking | null {
    const last = this.samples.at(-1); if (!last || now - last.at > 500) return null;
    const target = now - 100;
    let a = this.samples[0], b = a;
    for (const sample of this.samples) { b = sample; if (sample.at >= target) break; a = sample; }
    const t = a.at === b.at ? 1 : Math.max(0, Math.min(1, (target - a.at) / (b.at - a.at)));
    // Missing tracking takes effect immediately, rather than holding a stale hand.
    return { v: 1, time: b.pose.time, referenceHeight: last.pose.referenceHeight, head: blendPose(a.pose.head, b.pose.head, t), left: last.pose.left && a.pose.left && b.pose.left ? blendPose(a.pose.left, b.pose.left, t) : null, right: last.pose.right && a.pose.right && b.pose.right ? blendPose(a.pose.right, b.pose.right, t) : null };
  }
}
